import { useState } from "react";
import { motion } from "framer-motion";
import { AppleLogo, GooglePlayLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../lib/api";

const APPSTORE = "https://apps.apple.com/gb/app/my-local-ordering/id1468964983";
const PLAYSTORE = "https://play.google.com/store/apps/details?id=com.mylocal.ordering";

export default function DownloadCTA() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter({ email });
      toast.success("You're on the list", { description: "We'll only send the occasional local gem." });
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe right now", {
        description: err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="download"
      data-testid="download-cta-section"
      className="relative bg-[#FAFAFA] py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden rounded-[32px] md:rounded-[48px] bg-[#0A0A0A] text-white p-8 md:p-16 lg:p-20 grain">
          <div className="absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full brand-gradient opacity-30 blur-3xl" />
          <div className="absolute -bottom-32 -left-24 h-[380px] w-[380px] rounded-full bg-[#FF1F8B] opacity-20 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="overline text-[#FF1F8B] mb-5">Get the app</p>
              <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="font-display font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]"
              >
                This is how people <br />
                <span className="gradient-text">order locally now.</span>
              </motion.h2>
              <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
                500,000+ orders and counting.
              </p>
              <p className="mt-2 text-lg text-zinc-300 max-w-xl leading-relaxed font-semibold">
                just tap &amp; wait.
              </p>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href={APPSTORE}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cta-appstore-btn"
                  className="inline-flex items-center gap-3 bg-white text-zinc-900 px-6 py-4 rounded-2xl hover:-translate-y-0.5 transition-transform"
                >
                  <AppleLogo size={26} weight="fill" />
                  <div className="text-left leading-tight">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      Download on the
                    </div>
                    <div className="font-semibold -mt-0.5">App Store</div>
                  </div>
                </a>
                <a
                  href={PLAYSTORE}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cta-playstore-btn"
                  className="inline-flex items-center gap-3 bg-white/[0.06] border border-white/10 text-white px-6 py-4 rounded-2xl hover:bg-white/[0.1] hover:-translate-y-0.5 transition-all"
                >
                  <GooglePlayLogo size={24} weight="fill" />
                  <div className="text-left leading-tight">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">
                      Get it on
                    </div>
                    <div className="font-semibold -mt-0.5">Google Play</div>
                  </div>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-6 md:p-8 backdrop-blur-sm">
                <p className="overline text-zinc-400 mb-3">Stay in the loop</p>
                <h3 className="font-display font-extrabold text-xl md:text-2xl tracking-tight mb-5">
                  Hear about new locals first.
                </h3>
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
                  <input
                    type="email"
                    required
                    placeholder="you@local.co"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="newsletter-email-input"
                    className="flex-1 bg-black/30 border border-white/10 rounded-full px-5 py-3.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FF1F8B] transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="newsletter-submit-btn"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full brand-gradient text-white font-semibold disabled:opacity-60 hover:-translate-y-0.5 transition-transform"
                  >
                    {loading ? "Joining…" : "Join"}
                    <PaperPlaneTilt size={16} weight="bold" />
                  </button>
                </form>
                <p className="mt-4 text-xs text-zinc-500">
                  A quiet note when a new local joins. Nothing else.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
